<section id="baseline" [clrLoading]="loading">
  <clr-alert
    *ngIf="calibrationJustChanged"
    [clrAlertType]="'info'"
    [clrAlertClosable]="true"
  >
    <clr-alert-item>
      <span class="alert-text">
        The Last Calibration Date was changed. The baseline below is the new
        one and applies to every test evaluated from now on; the tests listed
        with a previous baseline can be re-evaluated against it.
      </span>
    </clr-alert-item>
  </clr-alert>

  <div *ngIf="loadError" class="alert alert-warning" role="alert">
    <div class="alert-items">
      <div class="alert-item static">
        <span class="alert-text">
          Tests since calibration are unavailable: {{ loadError }}
        </span>
      </div>
    </div>
  </div>

  <div *ngFor="let status of statuses" class="card">
    <div class="card-header">
      Tests since calibration: {{ status.routineName }}
      <span class="card-subtitle" *ngIf="status.calibrationDate">
        calibration date {{ status.calibrationDate }}
      </span>
      <span class="card-subtitle" *ngIf="!status.calibrationDate">
        no calibration date recorded: every test counts
      </span>
    </div>
    <div class="card-block">
      <h5 class="card-title">Current baseline</h5>
      <p class="card-text" *ngIf="status.provisional">
        Provisional: {{ status.baselineStudyIds.length }} of 5 studies since
        the calibration; the baseline moves until the fifth study is tested.
      </p>
      <p class="card-text" *ngIf="!status.provisional">
        The average of the first five studies since the calibration (study ids
        {{ status.baselineStudyIds.join(', ') }}). Every evaluation from now on
        uses these numbers.
      </p>
      <table class="table table-compact">
        <thead>
          <tr>
            <th class="left">Measurement</th>
            <th class="left">Baseline</th>
            <th class="left">Warning band (±10 %)</th>
            <th class="left">Fail band (±15 %)</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let value of status.values">
            <td class="left">{{ value.key }}</td>
            <td class="left">{{ formatNumber(value.baseline, value.key) }}</td>
            <td class="left">
              {{ formatNumber(value.problemLow, value.key) }} –
              {{ formatNumber(value.problemHigh, value.key) }}
            </td>
            <td class="left">
              {{ formatNumber(value.low, value.key) }} –
              {{ formatNumber(value.high, value.key) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="card-block">
      <h5 class="card-title">
        Tests since the calibration
        <span *ngIf="status.testCount > status.tests.length">
          (latest {{ status.tests.length }} of {{ status.testCount }})
        </span>
      </h5>
      <p class="card-text" *ngIf="status.staleCount > 0">
        {{ status.staleCount }} test{{ status.staleCount === 1 ? '' : 's' }}
        {{ status.staleCount === 1 ? 'was' : 'were' }} evaluated against a
        previous baseline (a test is evaluated against the baseline in force
        at the time; changing the calibration date does not change stored
        results).
      </p>
      <p class="card-text" *ngIf="status.staleCount === 0">
        Every test since the calibration was evaluated against the current
        baseline.
      </p>
      <table class="table table-compact">
        <thead>
          <tr>
            <th class="left">{{ 'Scan date' | term }}</th>
            <th class="left">Tested</th>
            <th class="left">Result</th>
            <th class="left">mAs baseline used</th>
            <th class="left">SDNR baseline used</th>
            <th class="left">State</th>
            <th class="left" *ngIf="canReevaluate"></th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let test of status.tests">
            <td class="left">
              <a [routerLink]="['/home/phantomTests', test.id]">{{
                formatDate(test.scanDate)
              }}</a>
            </td>
            <td class="left">{{ formatDate(test.dateCreated) }}</td>
            <td class="left">
              <span
                class="label"
                [ngClass]="{
                  'label-success': test.result === 'pass',
                  'label-warning': test.result === 'problem',
                  'label-danger': test.result === 'fail'
                }"
                >{{ test.result }}</span
              >
              <ng-container *ngIf="outcomeOf(test) as outcome">
                <span
                  *ngIf="outcome.result && outcome.result !== outcome.previousResult"
                  class="outcome"
                >
                  was {{ outcome.previousResult }}
                </span>
              </ng-container>
            </td>
            <td class="left">
              {{ formatNumber(evaluatedAgainst(test, 'mAs'), 'mAs') }}
            </td>
            <td class="left">
              {{ formatNumber(evaluatedAgainst(test, 'sdnr'), 'sdnr') }}
            </td>
            <td class="left">
              <span [ngClass]="{ stale: test.stale }">{{ stateOf(test) }}</span>
              <span *ngIf="outcomeOf(test)?.error" class="outcome-error">
                {{ outcomeOf(test).error }}
              </span>
            </td>
            <td class="left" *ngIf="canReevaluate">
              <button
                class="btn btn-sm btn-link"
                [disabled]="busy[test.id] || bulkBusy || test.status !== 'saved'"
                (click)="reevaluateOne(status, test)"
              >
                <clr-spinner *ngIf="busy[test.id]" clrInline></clr-spinner>
                Re-evaluate
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="card-footer" *ngIf="canReevaluate">
      <button
        class="btn btn-sm btn-primary-outline"
        [disabled]="bulkBusy || staleTests(status).length === 0"
        (click)="reevaluateAll(status)"
      >
        <clr-spinner *ngIf="bulkBusy" clrInline></clr-spinner>
        Re-evaluate all against the current baseline ({{
          staleTests(status).length
        }})
      </button>
    </div>
  </div>
</section>
